import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/admin/ui/card';
import { Badge } from '@/components/admin/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/admin/ui/table';
import { Input } from '@/components/admin/ui/input';
import { Label } from '@/components/admin/ui/label';
import { Switch } from '@/components/admin/ui/switch';
import { Button } from '@/components/admin/ui/button';
import { supabaseAdmin } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { AlertCircle, ChevronDown, ChevronRight, Save, Settings } from 'lucide-react';

interface BookingAttempt {
  id: string;
  created_at: string;
  user_id: string | null;
  photographer_id: string;
  attempted_date: string;
  event_city: string | null;
  event_state: string | null;
  block_reason: string;
  days_until_event: number | null;
  user_agent: string | null;
  ip_address: string | null;
}

/** `embedded`: the new console's Booking Rules shows its own rule cards, so only the blocked-attempts list renders here. */
const BookingAttempts = ({ embedded = false }: { embedded?: boolean } = {}) => {
  const [attempts, setAttempts] = useState<BookingAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  // The attempts list is long; inside Business Controls it starts collapsed (Trera, 25 Sep 2026)
  const [open, setOpen] = useState(!embedded);

  // Settings state
  const [minimumDays, setMinimumDays] = useState(10);
  const [allowOverride, setAllowOverride] = useState(false);

  useEffect(() => {
    fetchBookingAttempts();
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data, error } = await supabaseAdmin
        .from('system_settings')
        .select('setting_key, setting_value')
        .in('setting_key', ['minimum_booking_days', 'allow_short_notice_bookings']);

      if (error) throw error;

      if (data) {
        const minDaysSetting = data.find(s => s.setting_key === 'minimum_booking_days');
        const allowOverrideSetting = data.find(s => s.setting_key === 'allow_short_notice_bookings');

        if (minDaysSetting) {
          setMinimumDays(parseInt(minDaysSetting.setting_value) || 10);
        }
        if (allowOverrideSetting) {
          setAllowOverride(allowOverrideSetting.setting_value === 'true');
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load settings',
        variant: 'destructive',
      });
    }
  };

  const fetchBookingAttempts = async () => {
    try {
      const { data, error } = await supabaseAdmin
        .from('booking_attempts')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      setAttempts(data || []);
    } catch (error) {
      console.error('Error fetching booking attempts:', error);
      toast({
        title: 'Error',
        description: 'Failed to load booking attempts',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      console.log('Saving settings:', { minimumDays, allowOverride });

      // Upsert minimum_booking_days
      const { data: minDaysData, error: minDaysError } = await supabaseAdmin
        .from('system_settings')
        .upsert(
          {
            setting_key: 'minimum_booking_days',
            setting_value: minimumDays.toString(),
            setting_type: 'number',
            description: 'Minimum number of days required before the event date',
            category: 'booking'
          },
          { onConflict: 'setting_key' }
        )
        .select();

      console.log('Upsert minimum_booking_days result:', { minDaysData, minDaysError });

      if (minDaysError) {
        console.error('Error upserting minimum_booking_days:', minDaysError);
        throw new Error(`Failed to save minimum_booking_days: ${minDaysError.message}`);
      }

      // Upsert allow_short_notice_bookings
      const { data: allowOverrideData, error: allowOverrideError } = await supabaseAdmin
        .from('system_settings')
        .upsert(
          {
            setting_key: 'allow_short_notice_bookings',
            setting_value: allowOverride.toString(),
            setting_type: 'boolean',
            description: 'Allow customers to book events with less than minimum days notice',
            category: 'booking'
          },
          { onConflict: 'setting_key' }
        )
        .select();

      console.log('Upsert allow_short_notice_bookings result:', { allowOverrideData, allowOverrideError });

      if (allowOverrideError) {
        console.error('Error upserting allow_short_notice_bookings:', allowOverrideError);
        throw new Error(`Failed to save allow_short_notice_bookings: ${allowOverrideError.message}`);
      }

      toast({
        title: 'Settings Saved',
        description: 'Booking settings have been updated successfully.',
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const getBlockReasonBadge = (reason: string) => {
    if (reason.includes('minimum')) {
      return <Badge variant="destructive" className="whitespace-nowrap text-white">{reason}</Badge>;
    }
    return <Badge variant="secondary" className="whitespace-nowrap text-white">{reason}</Badge>;
  };

  return (
    <div className={embedded ? 'space-y-6' : 'p-8 space-y-6'}>
      {!embedded && (<>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-foreground">Booking Attempts</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage booking restrictions and view blocked booking attempts
        </p>
      </div>

      {/* Settings Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            <CardTitle>Booking Restrictions</CardTitle>
          </div>
          <CardDescription>
            Configure minimum booking days and override settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Minimum Days Setting */}
            <div className="space-y-2">
              <Label htmlFor="minimumDays">Minimum Booking Days</Label>
              <Input
                id="minimumDays"
                type="number"
                min="0"
                value={minimumDays}
                onChange={(e) => setMinimumDays(parseInt(e.target.value) || 0)}
                className="max-w-xs"
              />
              <p className="text-sm text-muted-foreground">
                Minimum number of days required before the event date
              </p>
            </div>

            {/* Allow Override Setting */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="allowOverride">Allow Short Notice Bookings</Label>
                <Switch
                  id="allowOverride"
                  checked={allowOverride}
                  onCheckedChange={setAllowOverride}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                When enabled, customers can book events with less than the minimum days notice
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={handleSaveSettings}
              disabled={saving}
              className="gap-2"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <AlertCircle className="h-4 w-4" />
              Note: Settings changes will take effect immediately
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Blocked Attempts Table */}
      </>)}
      <Card>
        <CardHeader>
          <button type="button" aria-expanded={open} onClick={() => setOpen(!open)} className="flex w-full items-start gap-2 text-left">
            {open ? <ChevronDown className="mt-1 h-5 w-5 shrink-0" /> : <ChevronRight className="mt-1 h-5 w-5 shrink-0" />}
            <span>
              <CardTitle>Blocked Booking Attempts{!loading && <span className="ml-2 text-sm font-normal text-muted-foreground">({attempts.length})</span>}</CardTitle>
              <CardDescription>
                Recent booking attempts that were blocked due to restrictions{open ? '' : ' · click to show'}
              </CardDescription>
            </span>
          </button>
        </CardHeader>
        {open && <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading booking attempts...</div>
          ) : attempts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No blocked attempts found</div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date/Time</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Photographer</TableHead>
                    <TableHead>Event Date</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Days Until Event</TableHead>
                    <TableHead>Block Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {attempts.map((attempt) => (
                    <TableRow key={attempt.id}>
                      <TableCell className="whitespace-nowrap">
                        {format(new Date(attempt.created_at), 'MMM d, yyyy h:mm a')}
                      </TableCell>
                      <TableCell>
                        {attempt.user_id ? (
                          <span className="text-sm font-mono">{attempt.user_id.substring(0, 8)}...</span>
                        ) : (
                          <span className="text-muted-foreground">Anonymous</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-mono">{attempt.photographer_id.substring(0, 8)}...</span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {format(new Date(attempt.attempted_date), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell>
                        {attempt.event_city && attempt.event_state ? (
                          <span className="whitespace-nowrap">
                            {attempt.event_city}, {attempt.event_state}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">N/A</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {attempt.days_until_event !== null ? (
                          <Badge
                            variant={attempt.days_until_event < 10 ? "destructive" : "secondary"}
                            className="whitespace-nowrap text-white"
                          >
                            {attempt.days_until_event} days
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">N/A</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {getBlockReasonBadge(attempt.block_reason)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>}
      </Card>
    </div>
  );
};

export default BookingAttempts;
